import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { action, computed, makeObservable, runInAction } from 'mobx';
import { inject, Observer, observer } from 'mobx-react';
import * as React from 'react';
import { withRouter } from 'react-router';
import { Redirect, RouteComponentProps, Switch } from 'react-router-dom';
import { toast } from 'sonner';
import * as Models from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import {
	EDuplicateType,
	shouldShowDuplicateTemplateWarning,
	suppressDuplicateTemplateWarning,
} from '../../../../models/Campaigns';
import { hideDefaultMessageTemplate } from '../../../../models/MessageTemplates';
import { getDisplayName } from '../../../../models/NameUtils';
import { FirstNamePlaceholder, Token } from '../../../../models/Token';
import {
	accountHasComplianceEnabled,
	applyDefaultExcludedTagsToContactsFilterRequest,
	convertRawRichTextContentStateToRichContentEditorState,
	getDefaultBulkMessagingBodyEditorState,
	getFileSizeStringValue,
	getKeyDateKindFromResourceSelectorId,
} from '../../../../models/UiUtils';
import {
	ComposeEmailError,
	ComposeEmailViewModel,
	ComposeResourceEmailViewModel,
} from '../../../../viewmodels/AppViewModels';
import hbdImageGraphic from '../../../assets/hbd_action_graphic.svg';
import { AiButton } from '../../../components-v2/AiButton';
import { Attachments } from '../../../components/Attachments';
import { AutomationPreview } from '../../../components/automation/AutomationPreview';
import { Avatar } from '../../../components/Avatar';
import { DuplicateWarningDialog } from '../../../components/campaigns/DuplicateWarningDialog';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../components/ConfirmationDialog';
import { DeleteConfirmation } from '../../../components/DeleteConfirmation';
import { DeprecatedPopover, PopoverType } from '../../../components/DeprecatedPopover';
import { ISelectOption } from '../../../components/DeprecatedSelect';
import { AlternateKeyDatesHeader } from '../../../components/email/AlternateKeyDatesHeader';
import { SimpleEmailComposer } from '../../../components/email/EmailComposer';
import {
	IPlaceholderPopoverProps,
	MergeFieldPopOverMessage,
} from '../../../components/email/EmailComposer/presentation';
import { TagEmailRecipientsList } from '../../../components/email/TagEmailRecipientsList';
import { EmailMessageTemplateButtons } from '../../../components/email/templates/EmailMessageTemplateButtons';
import { HandwrittenCardTemplateSearchModal } from '../../../components/HandwrittenCards/HandwrittenCardTemplateSearchModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { PrivateRoute } from '../../../components/PrivateRoute';
import { RichContentDocumentEditor } from '../../../components/richContent/RichContentDocumentEditor';
import { ISendFromAdminsDropdownProps, SendFromAdminsDropdown } from '../../../components/SendFromAdminsDropdown';
import { SendFromDropdown } from '../../../components/SendFromDropdown';
import { SocialMediaBannerGraphic } from '../../../components/svgs/graphics/SocialMediaBannerGraphic';
import { ExpiryIcon } from '../../../components/svgs/icons/ExpiryIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { SystemTemplateTranslationSelector } from '../../../components/SystemTemplateTranslationSelector';
import { getTemplateApprovalStatus } from '../../../components/TemplateReviewStatusIndicator';
import { TemplateTypeTag } from '../../../components/templates/TemplateTypeTag';
import { CampaignSessionManager } from '../../../containers/shared/AIScheduleSuccess/campaignSessionManager';
import {
	clearGlobalAISourceData,
	shouldOpenAIDrawer,
} from '../../../containers/shared/AIScheduleSuccess/campaignSessionStore';
import { baseStyleSheet } from '../../../styles/styles';
import { UniversalAssistantChatWithinComposer } from '../AssistantChat';
import { buildContactFilterFromAudienceInfo } from '../BrowseAndSendCampaigns/audienceHelpers';
import { FollowUpContentSelector } from '../FollowUpContentSelector';
import { Search } from '../Search';
import { IConnectionTypeComponentProps, withConnectionTypes } from './context';
import {
	EditCampaignContentCreationTips,
	EditCampaignEmailFollowUpRecipients,
	EditCampaignRecipients,
	EditCampaignSurveyFollowUpRecipientOptions,
	EditCampaignSurveyFollowUpRecipients,
	StartAutomationBanner,
} from './presentation';
import { styleSheet } from './styles';

const LazyHtmlNewsletterCampaignComposer = React.lazy(
	() => import(/* webpackPrefetch: true */ '../../../components/HtmlNewsletterCampaignComposer')
);

const NavigateConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: "Yes, don't save",
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Stay on this campaign',
	},
];

const adminRegExp = new RegExp(/admin/i);
const customRegExp = new RegExp(/custom/i);
const TurningXXRexExp = new RegExp(/xx/i);

export interface IEditCampaignComponent {
	finishEditingContent?(): void;
	resetSignature?(userId?: string): void;
	validateBaseEmailBeforeSending?(): boolean;
}

interface IProps
	extends IEventLoggingComponentProps,
		RouteComponentProps<any>,
		IUserSessionComponentProps,
		IErrorMessageComponentProps,
		Models.IImpersonationContextComponentProps,
		IFullscreenModalComponentProps,
		IConnectionTypeComponentProps {
	canImpersonateOnTemplateSave?: boolean;
	canShowTemplateSelect?: boolean;
	className?: string;
	contextFinishedEditingCtaText?: string;
	disableCampaignEdits?: boolean;
	disableContactEdits?: boolean;
	editing?: boolean;
	emailComposer: ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>;
	initialEmailBodyTemplate?: Api.ITemplate;
	isQueuedCampaign?: boolean;
	onAutomationsCreated?(result: Api.ICreateAutomationsResult): void;
	onContentFinishedEditingClicked?(e: React.MouseEvent<HTMLElement>): void;
	onContextFinishedEditingClicked?(e: React.MouseEvent<HTMLElement>): void;
	onInnerRef?(ref?: IEditCampaignComponent): void;
	onRejectClicked?(): void;
	onShowOmitConfirmation?(e?: React.MouseEvent<HTMLElement>, hasChanged?: boolean): void;
	onSocialMediaPostBannerClicked?(e: React.MouseEvent<HTMLElement>): void;
	readonlyContent?: boolean;
	showForInitialSelection?: boolean;
	styles?: StyleDeclarationValue[];
	sendFromAdminsProps?: ISendFromAdminsDropdownProps;
}

interface IState {
	automationTemplatesForResourceSelectorId?: Api.IAutomationTemplate[];
	isLoadingAutomationTemplates?: boolean;
	bodyError?: string;
	canShowContentCreationTips?: boolean;
	duplicateType?: EDuplicateType;
	editing?: boolean;
	emailBodyEditorState?: Api.IRichContentEditorState;
	emailBodyTemplate?: Api.ITemplate;
	hasEdited?: boolean;
	hideTemplateSaveButton?: boolean;
	isDuplicateModalOpen?: boolean;
	isNewEmail?: boolean;
	isPriorFollowUp?: boolean;
	lastContactListScrollOffset?: number;
	levPlaceholderPopover?: IPlaceholderPopoverProps;
	navigateAway?(): void;
	newContactFirstName?: string;
	previousContactFirstName?: string;
	redirectTo?: string;
	selectedAutomationTemplate?: Api.IAutomationTemplate;
	selectedCustomEmailHasCustomContent?: boolean;
	selectedSendFromOption?: Api.ISendOnBehalfPermission;
	selectedEmployee?: Api.IUser;
	showDeleteConfirmation?: boolean;
	showHwcTemplateChooserModal?: boolean;
	showNavigateConfirmation?: boolean;
	showOmitConfirmation?: boolean;
	subject?: string;
	subjectError?: string;
	translatedEmailBodyTemplate?: Api.ITemplate;
	unsubscribeSignatureTemplate?: Api.ITemplate;
}

class _EditCampaign extends React.Component<React.PropsWithChildren<IProps>, IState> implements IEditCampaignComponent {
	private mMounted: boolean;
	private mRecipientListScrollerRef: HTMLElement;
	private mTemplates: Api.TemplatesViewModel;
	private mUnsubscribeTemplate: Api.UnsubscribeTemplateViewModel;

	constructor(props: IProps) {
		super(props);

		makeObservable<
			_EditCampaign,
			| 'sendingOnBehalf'
			| 'canShowAlternateHeader'
			| 'resourceEmailComposer'
			| 'shouldShowComposer'
			| 'showForApproval'
			| 'canEdit'
			| 'sendRequiresCompliance'
			| 'isFromHtmlNewsletterTemplate'
			| 'willAddTemplateToUser'
			| 'onContactSelected'
			| 'showRecipients'
			| 'saveToEmail'
		>(this, {
			keyDateKind: computed,
			sendingOnBehalf: computed,
			canShowAlternateHeader: computed,
			resourceEmailComposer: computed,
			shouldShowComposer: computed,
			showForApproval: computed,
			canEdit: computed,
			sendRequiresCompliance: computed,
			isFromHtmlNewsletterTemplate: computed,
			willAddTemplateToUser: computed,
			onContactSelected: action,
			showRecipients: action,
			saveToEmail: action,
		});

		this.mUnsubscribeTemplate = new Api.UnsubscribeTemplateViewModel(props.userSession).impersonate(
			props.impersonationContext
		);

		/**
		 * @NOTE This is used to differentiate between the create flow for a follow-up and the edit flow In the create and the
		 * edit flows we have a value for this.props.emailComposer.emailMessage.options.followUp.bulkEmailMessageId, but
		 * only in the edit flow do we have a value for this.props.emailComposer.emailMessage.options.followUp.statuses on
		 * init of the component. "On init" is also important. In the create flow statuses is null on init, but at the end
		 * it will have a value. Thus, we cannot do this check later or change this to a @computed getter either.
		 */
		const isPriorFollowUp = this.props.emailComposer?.emailMessage?.options?.followUp?.statuses?.length > 0;

		const isNewEmail =
			!props.initialEmailBodyTemplate &&
			!props.emailComposer?.emailMessage?.templateReference &&
			!isPriorFollowUp &&
			!props.emailComposer?.campaign?.isScheduledToSend;

		this.state = {
			canShowContentCreationTips: isNewEmail && !props.canShowTemplateSelect && !props.emailComposer?.reachOutInfo,
			editing:
				isNewEmail ||
				(this.keyDateKind
					? !Api.TemplatesViewModel.userHasTemplateForKeyDateKind(props.userSession, this.keyDateKind)
					: false),
			emailBodyEditorState: convertRawRichTextContentStateToRichContentEditorState(
				props.emailComposer?.emailMessage?.content
			),
			emailBodyTemplate: props.initialEmailBodyTemplate,
			hasEdited: false,
			isDuplicateModalOpen: false,
			isNewEmail,
			isPriorFollowUp,
			showNavigateConfirmation: false,
			subject: props.emailComposer?.emailMessage?.subject,
		};
	}

	public get keyDateKind() {
		const resourceVM = this.props.emailComposer as ComposeResourceEmailViewModel;
		return getKeyDateKindFromResourceSelectorId(resourceVM?.resourceSelector) ?? null;
	}

	public async componentDidMount() {
		const { onInnerRef, emailComposer } = this.props;
		const { emailBodyTemplate } = this.state;
		this.mMounted = true;
		onInnerRef?.(this);
		this.initNewTemplatesVm();
		if (!this.mTemplates?.impersonationContext || this.mTemplates.impersonationContext.user?.id) {
			this.mTemplates.signatureTemplates.reset();
			this.mTemplates.signatureTemplates.getNext().then(() => {
				if (!this.sendingOnBehalf && !emailComposer.emailMessage?.signatureTemplate && !emailComposer?.campaign) {
					// intent: in edit mode don't initialize active signature template from loaded signature templates
					// because it should be initialized as part of edit campaign loading in ComposeEmailViewModel.loadInstanceForCampaign
					const signatureTemplate = this.mTemplates.signatureTemplates.fetchResults.find(x => !!x.defaultTemplate);
					emailComposer.emailMessage.signatureTemplate = signatureTemplate ?? null;
				}
			});
		}
		this.loadUnsubscribeSignatureTemplate();

		// load the automation templates first
		await this.loadAutomationTemplates();
		if (!emailComposer.emailMessage.attachments) {
			emailComposer.emailMessage.setAttachments(new Api.AttachmentsToBeUploadedViewModel([]));
		}

		emailComposer.isUserEditingMessage = true;

		// Ref: https://github.com/Real-Magic/Levitate/issues/11737
		// Only worry about showing the duplicate warning if used by the the current user.
		const duplicateType = EDuplicateType.User;
		const showDuplicateWarning = shouldShowDuplicateTemplateWarning(emailBodyTemplate);
		if (showDuplicateWarning) {
			this.setState({
				isDuplicateModalOpen: showDuplicateWarning,
			});
		}
		this.setState({ duplicateType });

		if (emailComposer.sendFromOption) {
			this.setState({ selectedSendFromOption: emailComposer.sendFromOption });
		}
		if (
			emailComposer.isCustomAutomation(this.action) &&
			emailComposer.emailMessage?.isSendFromOption(Api.SendEmailFrom.ContactOwner)
		) {
			emailComposer.sendOnBehalf = true;
			emailComposer.sendAsEmployee = null;
			emailComposer.sendAsConnectionType = undefined;
		}

		// Auto-open AI assistant when navigating from success page with content conversion
		if (shouldOpenAIDrawer()) {
			this.onAIContentGenerationAssistantButtonClicked();
			// Clear the global AI source data once used
			clearGlobalAISourceData();
		}
	}

	public resetSignature = (userId?: string) => {
		if (!userId) {
			this.props.emailComposer.emailMessage.signatureTemplate = null;
			// Also clear the unsubscribe signature when clearing the default signature
			this.setState({ unsubscribeSignatureTemplate: null });
			return;
		}
		this.initNewTemplatesVm(userId);
		this.loadDefaultSignature();
	};

	public componentWillUnmount() {
		this.mMounted = false;
		this.props.onInnerRef?.(null);

		const { emailComposer } = this.props;
		if (emailComposer != null) {
			emailComposer.isUserEditingMessage = false;
		}
	}

	public render() {
		const { className, styles, match, location, userSession, children, history } = this.props;
		const { redirectTo, showNavigateConfirmation } = this.state;

		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} edit-campaign ${className || ''}`}>
				{this.renderFullscreenHeader()}
				<div className={css(styleSheet.leftContainer)}>
					<div className={css(styleSheet.templateContainer)}>
						{this.renderHeader()}
						<div className={css(styleSheet.templateContentContainer)}>
							{this.action === 'automation' ? this.renderAutomationPreview() : this.renderEmailContent()}
						</div>
					</div>
				</div>
				{children ? (
					<Switch location={location}>
						<PrivateRoute location={location} userSession={userSession}>
							<div className={css(styleSheet.contextContainer)}>{children}</div>
						</PrivateRoute>
					</Switch>
				) : null}
				{!children && this.showForApproval ? (
					<Switch location={location}>
						<PrivateRoute
							exact={true}
							path={`${match.url}/recipients`}
							location={location}
							render={this.onRenderRecipientsSelect}
							userSession={userSession}
						/>
					</Switch>
				) : null}
				{!children && !this.showForApproval ? (
					<Switch location={location}>
						<PrivateRoute exact={true} location={location} path={`${match.url}/tags`} userSession={userSession}>
							<div className={css(styleSheet.contextContainer)}>
								<Search
									onNextClick={explicitelyPreventContactFilterRequest => {
										if (explicitelyPreventContactFilterRequest) {
											this.props.emailComposer?.setAllowContactFilterTagSearchChangesOverride(false);
										}
										this.showRecipients();
									}}
								/>
							</div>
						</PrivateRoute>
						<PrivateRoute
							exact={true}
							location={location}
							path={`${match.url}/recipients/survey-follow-up/from-report`}
							userSession={userSession}
						>
							<div className={css(styleSheet.contextContainer)}>
								<EditCampaignSurveyFollowUpRecipientOptions />
							</div>
						</PrivateRoute>
						<PrivateRoute
							exact={true}
							location={location}
							path={`${match.url}/recipients/survey-follow-up`}
							userSession={userSession}
						>
							<div className={css(styleSheet.contextContainer)}>
								<EditCampaignSurveyFollowUpRecipients
									onContextFinishedEditingClicked={this.onContextFinishedEditingClicked}
								/>
							</div>
						</PrivateRoute>
						<PrivateRoute
							exact={true}
							location={location}
							path={`${match.url}/recipients/follow-up`}
							userSession={userSession}
						>
							<div className={css(styleSheet.contextContainer)}>
								<EditCampaignEmailFollowUpRecipients
									contextFinishedEditingCtaText={this.props.contextFinishedEditingCtaText}
									isPriorFollowUp={this.state.isPriorFollowUp}
									onContextFinishedEditingClicked={this.onContextFinishedEditingClicked}
									onFinishEditingContent={this.finishEditingContent}
								/>
							</div>
						</PrivateRoute>
						<PrivateRoute
							exact={true}
							location={location}
							path={`${match.url}/recipients/:id`}
							userSession={userSession}
						>
							<div className={css(styleSheet.contextContainer)}>
								{this.props.emailComposer?.selectedCustomEmail ? (
									<EditCampaignRecipients
										initialEdit={!(this.props.emailComposer.selectedCustomEmailContact?.emailAddresses?.length > 0)}
										onContactEditButtonClicked={this.onContactEditButtonClicked}
										styles={this.showForApproval ? [styleSheet.recipientsApproval] : undefined}
									>
										{this.renderApprovalContextFooter()}
									</EditCampaignRecipients>
								) : null}
							</div>
						</PrivateRoute>
						<PrivateRoute exact={true} location={location} path={`${match.url}/recipients`} userSession={userSession}>
							<div className={css(styleSheet.contextContainer)}>{this.onRenderRecipientsSelect()}</div>
						</PrivateRoute>
					</Switch>
				) : null}

				<UniversalAssistantChatWithinComposer
					isOpen={this.isAIAssistantOpen()}
					onUseAIEmail={this.onUseAIEmail}
					onClose={() => history.goBack()}
					emailContext={{
						subject: this.state.subject,
						body: this.state.emailBodyEditorState?.getRawRichTextContent(),
					}}
					impersonationContext={this.props.impersonationContext?.toJs()}
				/>

				{this.state.showHwcTemplateChooserModal ? (
					<HandwrittenCardTemplateSearchModal
						isOpen={true}
						onClose={() => this.setState({ showHwcTemplateChooserModal: false })}
						onTemplateClicked={template => {
							this.props.fullscreenModal.history.push({
								pathname: `/bulk-birthday-postcard/${template.id}`,
							});
						}}
						resourceSelectorId={this.resourceEmailComposer?.resourceSelector}
					/>
				) : null}
				<DeleteConfirmation
					bodyText='Are you sure you want to delete this template?'
					destructiveButtonText='Yes, Delete'
					isOpen={this.state.showDeleteConfirmation}
					onFinish={this.onDeleteTemplate}
				/>
				{redirectTo && <Redirect push={true} to={redirectTo} />}
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{
						isOpen: showNavigateConfirmation,
						onRequestClose: this.onNavigateConfirmationRequestClose,
					}}
					options={NavigateConfirmationOptions}
					title='Navigating away will erase any unsaved changes you have made to this message. Are you sure?'
				/>

				<DuplicateWarningDialog
					isOpen={this.state.isDuplicateModalOpen}
					onRequestClose={this.onIsDuplicateModalRequestClose}
					title='Campaign Duplicate'
					type={CampaignType.Email}
					duplicateType={this.state.duplicateType}
					lastUsedDate={this.state.emailBodyTemplate?.lastUsedByDate?.currentUser}
				/>
			</div>
		);
	}

	private initNewTemplatesVm = (selectedUserId?: string) => {
		const { emailComposer, impersonationContext, userSession } = this.props;

		let userId = selectedUserId;
		if (!userId) {
			const sendFromUserId = emailComposer.emailMessage.options?.sendEmailFromUser?.id;

			userId =
				emailComposer.emailMessage.options?.sendEmailFrom === Api.SendEmailFrom.SelectedUser && sendFromUserId
					? sendFromUserId
					: emailComposer.campaign?.creator?.id;
		}

		this.mTemplates = new Api.TemplatesViewModel(userSession, {
			userId,
			// cannot use impersonationContext and `forUserId` at the same time
			forUserId: !impersonationContext?.isValid ? userId : undefined,
		}).impersonate(impersonationContext);
	};

	private onIsDuplicateModalRequestClose = (suppress?: boolean, cancel?: boolean) => {
		const { fullscreenModal } = this.props;
		if (suppress) {
			suppressDuplicateTemplateWarning(this.state.emailBodyTemplate.id);
		}
		if (cancel) {
			this.setState({ isDuplicateModalOpen: false });
			// Clear session when modal is dismissed
			CampaignSessionManager.clearSession();
			fullscreenModal.dismissModal();
			return;
		}
		this.setState({ isDuplicateModalOpen: false });
	};

	private renderFullscreenHeader() {
		const { readonlyContent, emailComposer } = this.props;
		const { isNewEmail, emailBodyTemplate } = this.state;
		let fullscreenTitle: string = null;
		if (this.action === 'automation') {
			fullscreenTitle = 'View Campaign';
		} else if (isNewEmail) {
			fullscreenTitle = emailComposer?.isFollowUp ? 'Create a Follow-Up Campaign' : 'Create New Campaign';
		} else {
			const content = emailBodyTemplate?.content || emailComposer.emailMessage.content;
			if (content?.sourceFormat === Api.ContentSourceFormat.UnlayerHtmlNewsletter) {
				fullscreenTitle = 'View Campaign';
			} else if (readonlyContent && emailComposer?.isFollowUp) {
				fullscreenTitle = 'View Follow-Up Campaign';
			} else {
				fullscreenTitle = 'View Campaign';
			}
		}

		return (
			<MultiContainerHeader
				fullscreenHeader={fullscreenTitle}
				onFullscreenRequestBack={this.onHeaderBackButtonClicked}
				onFullscreenRequestClose={this.onCloseButtonClicked}
			/>
		);
	}

	private renderReadOnlyContent(hasActionsHeader = false) {
		const { userSession, emailComposer } = this.props;
		const { emailBodyEditorState, levPlaceholderPopover, subject, unsubscribeSignatureTemplate } = this.state;

		const editorConfig = {
			contentRawCss: Models.DefaultRichContentRawCSS,
			plugins: 'placeholders',
			useDefaultEmailPlaceholders: true,
		};

		return (
			<div
				className={css(
					styleSheet.readonlyTemplateContent,
					hasActionsHeader ? styleSheet.readonlyTemplateContentWithActionsHeader : null
				)}
			>
				<div className={css(styleSheet.readonlyTemplateContentHeader)}>
					<div className={css(baseStyleSheet.truncateText)}>
						<div>Subject</div>
						<div className={css(baseStyleSheet.truncateText, styleSheet.subject)}>{subject}</div>
					</div>
				</div>
				<div className={css(styleSheet.readonlyTemplateContentScroller)}>
					<div className={css(styleSheet.readonlyTemplateContentBody)}>
						<RichContentDocumentEditor
							config={editorConfig}
							contentState={emailBodyEditorState}
							onExecuteCommand={this.onReadonlyEditorExecuteCommand}
							readOnly={true}
							readonlyChildren={
								levPlaceholderPopover ? (
									<DeprecatedPopover
										anchor={
											<span
												style={{
													left: levPlaceholderPopover.coordinate.x,
													position: 'absolute',
													top: levPlaceholderPopover.coordinate.y,
												}}
											/>
										}
										dismissOnClickOutside={true}
										isOpen={true}
										preferredPlacement='right'
										onRequestClose={this.onDismissLevPopover}
										type={PopoverType.white}
									>
										<MergeFieldPopOverMessage
											dismiss={this.onDismissLevPopover}
											emailComposer={emailComposer}
											individualRecipient={
												emailComposer?.selectedCustomEmail ? emailComposer?.selectedCustomEmailContact : null
											}
											popoverProps={levPlaceholderPopover}
										/>
									</DeprecatedPopover>
								) : undefined
							}
							styles={[styleSheet.readonlyTemplateEditor]}
						/>
					</div>
					<div className={css(styleSheet.readonlyTemplateContentFooter)}>
						{emailComposer?.emailMessage?.signatureTemplate ? (
							<RichContentDocumentEditor
								config={{ contentRawCss: Models.DefaultRichContentRawCSS }}
								contentState={convertRawRichTextContentStateToRichContentEditorState(
									emailComposer.emailMessage.signatureTemplate.content
								)}
								readOnly={true}
								styles={[styleSheet.readonlyTemplateEditor]}
							/>
						) : emailComposer?.emailMessage?.options?.sendEmailFrom === Api.SendEmailFrom.ContactOwner ? (
							<div className={css(styleSheet.signaturePlaceholder)}>
								{'{{'} contact owner&apos;s default signature will appear here {'}}'}
							</div>
						) : null}
						{unsubscribeSignatureTemplate && emailComposer?.recipients?.length > 4 && !this.keyDateKind && (
							<div className={css(styleSheet.readonlyTemplateContentFooterUnsubscribe)}>
								<RichContentDocumentEditor
									config={{ contentRawCss: Models.DefaultRichContentRawCSS }}
									contentState={convertRawRichTextContentStateToRichContentEditorState(
										unsubscribeSignatureTemplate.content
									)}
									readOnly={true}
									styles={[styleSheet.readonlyTemplateEditor]}
								/>
							</div>
						)}
					</div>
					{(emailComposer?.emailMessage?.savedAttachments?.length > 0 ||
						emailComposer?.emailMessage?.attachments?.attachments?.length > 0) && (
						<div className={css(styleSheet.attachmentContainer)}>
							<Attachments
								attachments={emailComposer?.emailMessage?.savedAttachments?.map(
									x => new Api.FileAttachmentViewModel(userSession, '', x)
								)}
								newAttachments={emailComposer?.emailMessage?.attachments}
								disableOnClick={true}
								readonly={true}
							/>
						</div>
					)}
				</div>
			</div>
		);
	}

	private get sendingOnBehalf() {
		const { emailComposer } = this.props;
		return !emailComposer?.emailMessage?.isSendFromOption(Api.SendEmailFrom.CurrentUser);
	}

	private get canShowAlternateHeader(): boolean {
		const { emailComposer } = this.props;
		const { automationTemplatesForResourceSelectorId } = this.state;

		const isFromTemplate = !!emailComposer?.emailMessage?.templateReference?.templateId;
		/**
		 * @NOTE - This list doesnt take in to account the default template option. This is because
		 * the default endpoint returns a template for the resourceSelectorId, which is
		 * not necessarily the same as the template that is in the dropdown for the
		 * emailComposer. So when we have only two templates in the list, the
		 * selected template (ie: the default) may be one of them and thus when we filter the list we
		 * may still have a template option that is not being shown.
		 */
		const filteredTemplates = isFromTemplate
			? emailComposer?.alternateKeyDateTemplates?.filter(
					x => x.id !== emailComposer.emailMessage.templateReference.templateId
				)
			: [];
		// alternate list has templates besides the currently selected one.
		const hasAlternateTemplates = isFromTemplate
			? filteredTemplates?.length
			: emailComposer?.alternateKeyDateTemplates?.length > 0;
		const hasAutomationTemplates = automationTemplatesForResourceSelectorId?.length > 0;
		const hasValidOptions = hasAutomationTemplates || (hasAlternateTemplates && isFromTemplate);
		const selectedCustomEmail = emailComposer?.selectedCustomEmail;

		return hasValidOptions && !selectedCustomEmail;
	}

	// #region Header
	private renderHeader() {
		const { emailComposer, userSession, impersonationContext } = this.props;
		const { automationTemplatesForResourceSelectorId, emailBodyTemplate, translatedEmailBodyTemplate } = this.state;
		// intentionally disabling social media indicator while under impersonation context until we have compliance figured out
		const isSocialMedia =
			!impersonationContext?.isValid &&
			(emailBodyTemplate?.associatedTemplates?.find(x => x.templateType === Api.TemplateType.SocialMediaPost) ||
				emailComposer?.suggestion?.template?.associatedTemplates?.find(
					x => x.templateType === Api.TemplateType.SocialMediaPost
				));
		const isSocialMediaEnabled = userSession.account?.features?.socialMedia?.enabled;
		const formattedExpirationDate = emailComposer?.emailMessage?.options?.scheduledSend?.expirationDate
			? new Date(emailComposer?.emailMessage?.options?.scheduledSend?.expirationDate)
			: new Date(emailBodyTemplate?.schedule?.expirationDate);

		const brokerDealer =
			(impersonationContext?.isValid ? impersonationContext.account : null)?.additionalInfo?.brokerDealer ||
			userSession.account?.additionalInfo?.brokerDealer;

		const canShowTranslationOption =
			((impersonationContext?.isValid ? impersonationContext.account : null) || userSession.account)?.features
				?.translation?.enabled &&
			this.action !== 'automation' &&
			!emailComposer?.selectedCustomEmailContact &&
			(emailBodyTemplate?.scope === Api.TemplateScope.System ||
				emailBodyTemplate?.scope === Api.TemplateScope.Industry) &&
			brokerDealer !== Api.FinancialBrokerDealer.Cetera;

		const shouldRenderStartAutomationsBanner =
			userSession.account.features?.automation?.enabled &&
			!this.state.isLoadingAutomationTemplates &&
			(automationTemplatesForResourceSelectorId || '').length === 0 &&
			this.action !== 'automation' &&
			!(this.action === 'edit' && !!emailComposer?.selectedCustomEmailContact);

		return (
			<div className={css(styleSheet.templateContainerHeader)}>
				<div className={css(styleSheet.templateContainerHeaderRow)}>
					<div className={css(styleSheet.templateContainerHeaderRowWrapper)}>
						{this.canShowAlternateHeader ? this.renderAlternateKeyDatesHeader() : this.renderNormalHeader()}

						{shouldRenderStartAutomationsBanner
							? this.renderSendFromDropdown(emailComposer, styleSheet.sendFromDropdownContainerWithAutomationsBanner)
							: (emailComposer?.emailMessage?.options?.scheduledSend?.expirationDate ||
									emailBodyTemplate?.schedule?.expirationDate) && (
									<div className={css(styleSheet.expirationDateContainer)}>
										<ExpiryIcon />
										<span className={css(styleSheet.expirationDate)}>
											{`This email automatically stops sending after ${formattedExpirationDate.toLocaleDateString()}.`}
										</span>
									</div>
								)}
					</div>

					{this.keyDateKind === Api.KeyDateKind.Birthday ? (
						<div>
							<div
								className={css(styleSheet.hbdCard)}
								onClick={() => this.setState({ showHwcTemplateChooserModal: true })}
							>
								<img src={hbdImageGraphic} alt='' className={css(styleSheet.hbdCardGraphic)} />
								<p className={css(styleSheet.hbdCardText)}>Send a handwritten bday card</p>
							</div>
						</div>
					) : null}
					{shouldRenderStartAutomationsBanner || (isSocialMedia && isSocialMediaEnabled) ? (
						<div>
							{shouldRenderStartAutomationsBanner ? this.renderStartAutomationsBanner() : null}
							{isSocialMedia && isSocialMediaEnabled ? this.renderCreateSocialMediaPostBanner() : null}
						</div>
					) : null}
				</div>
				<div className={css(styleSheet.templateContainerHeaderRow)}>
					<div>
						{!shouldRenderStartAutomationsBanner
							? this.renderSendFromDropdown(emailComposer)
							: (emailComposer?.emailMessage?.options?.scheduledSend?.expirationDate ||
									emailBodyTemplate?.schedule?.expirationDate) && (
									<div className={css(styleSheet.expirationDateContainer)}>
										<ExpiryIcon />
										<span className={css(styleSheet.expirationDate)}>
											{`This email automatically stops sending after ${formattedExpirationDate.toLocaleDateString()}.`}
										</span>
									</div>
								)}
					</div>
					{canShowTranslationOption ? (
						<SystemTemplateTranslationSelector
							onTranslatedTemplateSelected={this.onTranslatedSystemTemplateSelected}
							originalTemplate={emailBodyTemplate}
							selectedTemplate={translatedEmailBodyTemplate}
						/>
					) : null}
				</div>
			</div>
		);
	}

	private renderAlternateKeyDatesHeader() {
		const { emailBodyTemplate, automationTemplatesForResourceSelectorId, selectedAutomationTemplate } = this.state;
		const { emailComposer, initialEmailBodyTemplate } = this.props;
		const selector = (emailComposer as ComposeResourceEmailViewModel).resourceSelector;
		let emailTemplate: Api.ITemplate = initialEmailBodyTemplate;
		if (selector?.startsWith('Custom') || selector?.includes('XX')) {
			emailTemplate = { content: null, name: 'New Email' };
		}

		return (
			<AlternateKeyDatesHeader
				alternateKeyDateTemplates={emailComposer?.alternateKeyDateTemplates}
				automationTemplates={automationTemplatesForResourceSelectorId}
				emailMessage={emailComposer?.emailMessage}
				initialEmailBodyTemplate={emailTemplate}
				onSetTemplate={this.setTemplate}
				selectedTemplate={this.action === 'automation' ? selectedAutomationTemplate : emailBodyTemplate}
			/>
		);
	}

	private renderNormalHeader() {
		const { emailComposer, match, location } = this.props;
		const { emailBodyTemplate, isNewEmail } = this.state;

		return (
			<div>
				<div className={css(baseStyleSheet.truncateText)}>
					{isNewEmail ? (
						emailComposer?.isFollowUp ? (
							'Follow-Up Campaign'
						) : (
							'New Email'
						)
					) : (
						<div>
							<div className={css(styleSheet.nameContainer)}>
								<div className={css(baseStyleSheet.truncateText)}>
									{emailBodyTemplate?.name || emailComposer?.emailMessage?.subject}
									<span className={css(styleSheet.reviewStatus)}>
										{getTemplateApprovalStatus(emailBodyTemplate?.templateReview)}
									</span>
								</div>
								{this.isFromHtmlNewsletterTemplate && (
									<TemplateTypeTag styles={[styleSheet.templateTypeTag]} template={emailBodyTemplate} />
								)}
							</div>
							{emailComposer?.selectedCustomEmailContact && (
								<div className={css(styleSheet.selectedContactHeader)}>
									<Avatar
										className={css(styleSheet.selectedContactHeaderAvatar)}
										entityVm={emailComposer?.selectedCustomEmailContact}
									/>
									<div className={css(styleSheet.selectedContactHeaderDetails)}>
										<div>{emailComposer?.selectedCustomEmailContact.name}</div>
										<div className={css(baseStyleSheet.truncateText, styleSheet.selectedContactHeaderDetailsMessage)}>
											{`You are currently customizing the email for just ${getDisplayName(
												emailComposer?.selectedCustomEmailContact?.toJs(),
												true
											)}`}
										</div>
									</div>
								</div>
							)}
						</div>
					)}
				</div>
				{isNewEmail && `${match.url}/tags` === location?.pathname && (
					<div className={css(styleSheet.templateContainerHeaderNewEmailMessage)}>
						Start by writing your email content below, then select the recipients you want by selecting one or multiple
						tags.
					</div>
				)}
			</div>
		);
	}

	private renderSendFromDropdown(
		emailComposer: ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>,
		styleDeclaration?: StyleDeclarationValue
	) {
		if (this.props.sendFromAdminsProps) {
			return <SendFromAdminsDropdown {...this.props.sendFromAdminsProps} sendOnBehalfOf={this.sendOnBehalfOfAdmins} />;
		}

		return (
			<SendFromDropdown
				styles={styleDeclaration ? [styleDeclaration] : []}
				disabled={emailComposer?.disableCanSendOnBehalf}
				allowedContactBasedOptions='all'
				hideOptionsWithFilters={!emailComposer.allowContactFilterTagSearchChanges}
				sendOnBehalfOf={this.sendOnBehalfOf}
				sendFromOption={this.state.selectedSendFromOption}
				canSendOnBehalf={emailComposer && emailComposer.canSendOnBehalf && !emailComposer.isSuggestion}
				resourceSelectorId={this.resourceEmailComposer?.resourceSelector}
			/>
		);
	}

	private renderCreateSocialMediaPostBanner() {
		const { emailBodyTemplate } = this.state;
		return (
			<div className={css(styleSheet.socialMediaBannerContainer)}>
				<button
					onClick={
						emailBodyTemplate?.associatedTemplates?.length > 0
							? this.onEditSocialMediaPostClicked
							: this.onCreateSocialMediaPostClicked
					}
				>
					<SocialMediaBannerGraphic />
					<span className={css(styleSheet.bannerText)}>Available for social media!</span>
				</button>
			</div>
		);
	}

	private onTranslatedSystemTemplateSelected = (template: Api.ITemplate) => {
		const { emailComposer } = this.props;
		const { emailBodyTemplate } = this.state;
		if (template) {
			const { emailMessage } = emailComposer;
			emailMessage.subject = template.subject ?? emailMessage.subject;
			emailMessage.templateReference = {
				isCustomized: false,
				isSystemTemplate: template.scope === Api.TemplateScope.System || template.scope === Api.TemplateScope.Industry,
				templateId: template.id,
				name: template.name,
			};
			this.setState({
				emailBodyEditorState: convertRawRichTextContentStateToRichContentEditorState(template.content),
				subject: emailMessage.subject,
				translatedEmailBodyTemplate: emailBodyTemplate?.id === template?.id ? null : template,
			});
		}
	};

	private onAIContentGenerationAssistantButtonClicked = () => {
		const { history, match } = this.props;
		history?.push({
			pathname: `${match.url}/ai-content-generation-wizard`,
			state: history.location.state,
		});
	};

	private onUseAIEmail = (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		const { emailComposer, logApiError, errorMessages, userSession } = this.props;
		if (emailComposer.selectedCustomEmail) {
			// Fix for the losing customizations after applying an ai email to a custom email and clicking save and go back.
			// Seems like this state needs to be set to true whenever customizations happen to a custom email.
			this.setState({ selectedCustomEmailHasCustomContent: true });
		} else if (!emailComposer.selectedCustomEmail) {
			emailComposer.emailMessage.content = content;
			emailComposer.emailMessage.subject = subject;
			emailComposer.emailMessage.aiReference = aiReference;
		}

		// Apply audience info if present
		if (audienceInfo) {
			this.applyAudienceInfo(audienceInfo, userSession);
		}

		this.setState({
			emailBodyEditorState: convertRawRichTextContentStateToRichContentEditorState(content),
			subject,
		});

		if (this.showForApproval) {
			emailComposer.updateCampaign()?.catch(error => {
				logApiError('UpdateCampaign-Error', error);
				errorMessages.pushApiError(error);
			});
		}
	};

	private applyAudienceInfo = (audienceInfo: Api.ContactAudienceInfo, userSession: Api.UserSessionContext) => {
		const { emailComposer } = this.props;

		// Apply tag filters using shared helper
		const filterRequest = buildContactFilterFromAudienceInfo(audienceInfo);
		if (filterRequest) {
			emailComposer.emailMessage.contactsFilterRequest = filterRequest;
		}

		// Apply contactIds if present
		if (audienceInfo.contactIds?.length) {
			const contactViewModels = audienceInfo.contactIds.map(
				(id: string) => new Api.ContactViewModel(userSession, { id } as Api.IContact)
			);
			emailComposer.emailMessage.contactsToAdd.addAll(contactViewModels);
		}
	};

	private onCreateSocialMediaPostClicked = (e: React.MouseEvent<HTMLElement>) => {
		const { history, onSocialMediaPostBannerClicked } = this.props;
		onSocialMediaPostBannerClicked?.(e);
		if (e.isDefaultPrevented()) {
			return;
		}
		history.replace({
			pathname: '/social-media/post/create',
		});
	};

	private onEditSocialMediaPostClicked = (e: React.MouseEvent<HTMLElement>) => {
		const { history, onSocialMediaPostBannerClicked } = this.props;
		const { emailBodyTemplate } = this.state;
		onSocialMediaPostBannerClicked?.(e);
		if (e.isDefaultPrevented()) {
			return;
		}
		if (emailBodyTemplate?.associatedTemplates?.length > 0) {
			const socialMediaTemplateReference = emailBodyTemplate?.associatedTemplates?.find(
				x => x.templateType === Api.TemplateType.SocialMediaPost
			);
			const state: Models.ILocationState<void, Models.ICreateSocialMediaPostRequest<string>> = {
				model: {
					context: socialMediaTemplateReference?.id,
					type: 'TemplateId',
				},
			};
			history.replace({
				pathname: `/social-media/post/create/from-template`,
				state,
			});
			return;
		}
	};

	private onFollowUpContentSelected = (followUpType: Api.FollowUpType, template?: Api.ITemplate) => {
		const { emailComposer } = this.props;
		let bodyTemplate: Api.IRichContentEditorState = null;

		if (followUpType === Api.FollowUpType.New) {
			emailComposer.emailMessage.subject = null;
			emailComposer.emailMessage.content = null;
			bodyTemplate = getDefaultBulkMessagingBodyEditorState();
		} else if (followUpType === Api.FollowUpType.OriginalContent || followUpType === Api.FollowUpType.FromTemplate) {
			emailComposer.emailMessage.subject = template.subject;
			emailComposer.emailMessage.content = template.content;
			emailComposer.emailMessage.templateReference = template?.id
				? {
						isCustomized: false,
						isSystemTemplate:
							template.scope === Api.TemplateScope.System || template.scope === Api.TemplateScope.Industry,
						templateId: template.id,
						name: template.name,
					}
				: null;
			bodyTemplate = convertRawRichTextContentStateToRichContentEditorState(emailComposer.emailMessage.content);
		}
		this.setState({
			emailBodyEditorState: bodyTemplate,
			emailBodyTemplate: template,
			subject: emailComposer.emailMessage.subject,
		});
	};

	private resetGroupByHouseholdFromCurrentAutomationTemplate = (template: Api.IAutomationTemplate) => {
		const newGroupByHousehold =
			template.sendToHousehold || this.props.userSession.account.features.households?.sendToHouseholdByDefault;
		if (this.props.emailComposer.emailMessage.groupByHousehold !== newGroupByHousehold) {
			this.props.emailComposer.emailMessage.groupByHousehold = newGroupByHousehold;
			this.resetAndReloadContactsList();
		}
	};

	private setTemplate = async (template: Api.ITemplate | Api.IAutomationTemplate) => {
		const { emailComposer, userSession } = this.props;

		// Check if it's an automation template. Handles:
		// 1. IAutomationTemplateReference - has _type containing "Automation"
		// 2. IAutomationTemplate - has templateType === TemplateType.Automation
		// 3. AutomationTemplateViewModel (plugin) - has mModel with templateType
		const isAutomationTemplate = (() => {
			const templateAny = template as any;

			// Check _type for automation-related types (e.g., "AutomationTemplateReference", "AutomationTemplate")
			if (templateAny._type && typeof templateAny._type === 'string' && templateAny._type.includes('Automation')) {
				return true;
			}

			// Check if templateType is directly accessible (new ViewModel or raw interface)
			if ('templateType' in template && templateAny.templateType === Api.TemplateType.Automation) {
				return true;
			}

			// Check if it's a legacy ViewModel with mModel (plugin ViewModel pattern)
			if ('mModel' in template && templateAny.mModel?.templateType === Api.TemplateType.Automation) {
				return true;
			}

			return false;
		})();

		if (isAutomationTemplate) {
			const automationTemplate = template as Api.IAutomationTemplate;
			// check that sendfrom isn't some random user... if it is, reset to contact owner and update recipients list
			if (emailComposer.emailMessage.options.sendEmailFrom === Api.SendEmailFrom.SelectedUser) {
				const contactOwnerMode = userSession.user.defaultSendOnBehalfPermissions?.find(
					x => x.sender?.mode === Api.SendEmailFrom.ContactOwner
				);
				if (contactOwnerMode) {
					await this.sendOnBehalfOf(contactOwnerMode);
				}
			}
			this.resetGroupByHouseholdFromCurrentAutomationTemplate(automationTemplate);
			this.setState({
				selectedAutomationTemplate: automationTemplate,
			});
			emailComposer.canCustomizeIndividualEmails = false;
		} else {
			const emailTemplate = template as Api.ITemplate;
			emailComposer?.emailMessage?.setContentWithTemplate(emailTemplate);
			if (emailComposer.emailMessage.options.sendEmailFrom === Api.SendEmailFrom.ContactOwner) {
				const contactOwnerMode = userSession.user.defaultSendOnBehalfPermissions?.find(
					x => x.sender?.mode === Api.SendEmailFrom.ContactOwner
				);
				if (contactOwnerMode) {
					await this.sendOnBehalfOf(contactOwnerMode);
				}
			}
			emailComposer.canCustomizeIndividualEmails = true;

			this.setState({
				emailBodyEditorState: convertRawRichTextContentStateToRichContentEditorState(emailTemplate.content),
				emailBodyTemplate: emailTemplate,
				selectedAutomationTemplate: null,
				subject: emailTemplate.subject,
			});
		}
	};

	private sendOnBehalfOfAdmins = (
		option: ISelectOption<string | Api.UserViewModel<Api.IUser>>,
		user?: Api.UserViewModel<Api.IUser>
	) => {
		const { emailComposer } = this.props;
		const senderOption =
			option?.dataContext === Api.SendEmailFrom.ContactOwner
				? { sender: { mode: Api.SendEmailFrom.ContactOwner } }
				: { sender: { mode: Api.SendEmailFrom.SelectedUser, selectedUser: user?.id } };
		this.setState({ selectedSendFromOption: senderOption });
		emailComposer.sendFromOption = senderOption;
		this.props.sendFromAdminsProps.sendOnBehalfOf(option, user);
	};

	private sendOnBehalfOf = async (option: Api.ISendOnBehalfPermission, user?: Api.IUser) => {
		if (user && option.sender?.mode === Api.SendEmailFrom.SelectedUser) {
			this.onSaveSelectAnEmployee(option, user);
			return;
		}

		const { emailComposer, logEvent, userSession } = this.props;
		this.setState({ selectedSendFromOption: option });
		emailComposer.sendFromOption = option;

		if (emailComposer?.emailMessage?.options?.sendEmailFrom) {
			emailComposer.emailMessage.options.sendEmailFrom = option.sender?.mode;
			emailComposer.emailMessage.setSendEmailFromUser(undefined);

			if (emailComposer.allowContactFilterTagSearchChanges) {
				if (option.requiredFilter) {
					emailComposer.emailMessage.contactsFilterRequest.ownershipFilter = option.requiredFilter;
				} else if (option.sender.mode !== Api.SendEmailFrom.CurrentUser) {
					emailComposer.emailMessage.contactsFilterRequest.ownershipFilter = null;
				}
			}

			switch (option.sender?.mode) {
				case Api.SendEmailFrom.CurrentUser: {
					this.initNewTemplatesVm(emailComposer?.campaign?.creator?.id);
					this.loadDefaultSignature();
					this.resetAndReloadContactsList(true);
					if (emailComposer.isCustomAutomation(this.action)) {
						emailComposer.sendOnBehalf = false;
						emailComposer.sendAsEmployee = userSession!.user!.id!;
						emailComposer.sendAsConnectionType = undefined;
					}
					break;
				}
				case Api.SendEmailFrom.ContactOwner:
					if (emailComposer.isCustomAutomation(this.action)) {
						emailComposer.sendOnBehalf = true;
						emailComposer.sendAsEmployee = undefined;
						emailComposer.sendAsConnectionType = undefined;
					}
					this.resetSignature();
					break;
				case Api.SendEmailFrom.ConnectionType:
					emailComposer.sendAsConnectionType = option.sender?.connectionType;
					emailComposer.emailMessage.options.sendFromConnectionType = option.sender?.connectionType;
					if (emailComposer.isCustomAutomation(this.action)) {
						emailComposer.sendOnBehalf = true;
						emailComposer.sendAsEmployee = undefined;
					}
					this.resetAndReloadContactsList();
					break;
				default:
					break;
			}
		} else {
			logEvent?.('FailedToSetSendOnBehalfOption');
			toast.error(`Failed to set send on behalf option`);
		}
	};

	private onSaveSelectAnEmployee = async (option?: Api.ISendOnBehalfPermission, user?: Api.IUser) => {
		const { emailComposer } = this.props;
		this.setState({
			selectedSendFromOption: option,
			selectedEmployee: user,
			unsubscribeSignatureTemplate: null, // Clear unsubscribe signature when changing users
		});
		emailComposer.sendFromOption = option;

		this.mTemplates.signatureTemplates.reset();
		if (user) {
			this.initNewTemplatesVm(user.id);
			this.loadDefaultSignature();

			if (emailComposer?.emailMessage) {
				emailComposer.emailMessage.options.sendEmailFrom = option.sender.mode;
				emailComposer.emailMessage.setSendEmailFromUser(user);
				emailComposer.sendAsEmployee = user.id;
			}
			if (emailComposer.isCustomAutomation(this.action)) {
				emailComposer.sendAsEmployee = user.id;
				emailComposer.sendOnBehalf = false;
			}
		}

		this.resetAndReloadContactsList();
	};
	// #endregion

	// #region Automations
	private renderStartAutomationsBanner() {
		const { emailComposer, userSession } = this.props;
		return (
			<div>
				<StartAutomationBanner
					account={emailComposer?.impersonationContext?.account || userSession.account.toJs()}
					resourceSelectorId={this.resourceEmailComposer?.resourceSelector}
					onClick={this.onStartAutomationBannerClicked}
				/>
			</div>
		);
	}

	private renderAutomationPreview() {
		const { selectedAutomationTemplate } = this.state;
		return (
			<div className={css(styleSheet.automationPreview)}>
				<div className={css(styleSheet.automationPreviewActions)}>
					<button
						className={css(baseStyleSheet.ctaButtonReverseSmall, styleSheet.automationPreviewEditAutomationButton)}
						onClick={this.onEditAutomationClicked}
					>
						<span>Exit and edit automation</span>
					</button>
				</div>
				<AutomationPreview templateId={selectedAutomationTemplate?.id} />
			</div>
		);
	}

	private onEditAutomationClicked = () => {
		this.setState({
			navigateAway: () => {
				const { history } = this.props;
				const { selectedAutomationTemplate } = this.state;
				history.push({
					pathname: `/automations/${selectedAutomationTemplate.id}`,
				});
			},
			showNavigateConfirmation: true,
		});
	};
	// #endregion

	private get resourceEmailComposer() {
		const { emailComposer } = this.props;
		const result = emailComposer as ComposeResourceEmailViewModel;
		return result?.resourceSelector ? result : null;
	}

	private isAIAssistantOpen() {
		return this.props.location.pathname.includes(`ai-content-generation-wizard`);
	}

	private renderEmailContent() {
		const { emailBodyEditorState, emailBodyTemplate, subject, isPriorFollowUp } = this.state;
		const {
			canImpersonateOnTemplateSave,
			disableCampaignEdits,
			emailComposer,
			isQueuedCampaign,
			impersonationContext,
			userSession,
		} = this.props;

		const content = emailBodyTemplate?.content || emailComposer.emailMessage.content;
		const shouldRenderActions = !emailComposer?.selectedCustomEmail;
		const isFollowUp = emailComposer.isFollowUp;
		const isEmailFollowUp = emailComposer.followUpSource instanceof Api.CampaignViewModel;
		if (content?.sourceFormat === Api.ContentSourceFormat.UnlayerHtmlNewsletter) {
			// For scheduled campaigns, use campaign content instead of template content
			const templateToUse =
				emailComposer.campaign?.id && emailComposer.emailMessage.content
					? ({ content: emailComposer.emailMessage.content } as Api.ITemplate)
					: emailBodyTemplate;

			return (
				<React.Suspense fallback={this.onRenderHtmlNewsletterCampaignComposerLoading()}>
					<LazyHtmlNewsletterCampaignComposer
						onEditEmailClicked={this.onEditHtmlNewsletterTemplateClicked}
						onSubjectChanged={this.onSubjectChanged}
						readonly={emailComposer.campaign?.id && !isFollowUp}
						subject={subject}
						template={templateToUse}
					/>
				</React.Suspense>
			);
		}

		const accountHasAiGeneratedContentFeatureEnabled = (
			impersonationContext?.account ? impersonationContext.account : userSession.account
		).features?.contentGeneration?.enabled;

		const canShowAiContentAssistant =
			this.canEdit && accountHasAiGeneratedContentFeatureEnabled && this.action !== 'automation';

		return (
			<div className={css(styleSheet.emailContentContainer)}>
				<div className={css(styleSheet.emailHeaderContainer)}>
					<div className={css(styleSheet.emailHeaderContainerLeft)}>
						{canShowAiContentAssistant ? (
							<div className={css(styleSheet.aiAssistantButton)}>
								<AiButton
									onClick={this.onAIContentGenerationAssistantButtonClicked}
									disabled={this.isAIAssistantOpen()}
									title='Write email with our AI assistant'
									size='xs'
								/>
							</div>
						) : null}
					</div>
					<div className={css(styleSheet.emailHeaderContainerRight)}>
						{shouldRenderActions && isFollowUp && !isPriorFollowUp && isEmailFollowUp && (
							<FollowUpContentSelector
								emailComposer={emailComposer}
								onFollowUpContentSelected={this.onFollowUpContentSelected}
							/>
						)}
						{shouldRenderActions && (
							<EmailMessageTemplateButtons
								canDelete={isPriorFollowUp ? false : this.canDelete()}
								canImpersonateOnTemplateSave={canImpersonateOnTemplateSave}
								disableEditing={isPriorFollowUp ? true : disableCampaignEdits}
								editorState={emailBodyEditorState}
								emailMessage={emailComposer?.emailMessage}
								isEditing={this.shouldShowComposer}
								isQueuedCampaign={isQueuedCampaign}
								onDeleteClicked={this.onDeleteClick}
								onEditClicked={this.startEditing}
								onPreviewClicked={this.onFinishEditingContentClicked}
								onTemplateSaved={this.onBodyTemplateChanged}
								styles={[styleSheet.emailContentActionsHeader]}
								subject={subject}
								template={emailBodyTemplate}
								templates={this.mTemplates}
							/>
						)}
					</div>
				</div>
				{this.shouldShowComposer ? this.renderEmailComposer() : this.renderReadOnlyContent(shouldRenderActions)}
			</div>
		);
	}

	private onRenderHtmlNewsletterCampaignComposerLoading = () => {
		return <LoadingSpinner type='large' />;
	};

	private get shouldShowComposer() {
		const { editing, isPriorFollowUp } = this.state;
		const { readonlyContent } = this.props;
		return editing && !readonlyContent && !isPriorFollowUp;
	}

	private get showForApproval() {
		return this.props.emailComposer?.campaign?.status === Api.EmailSendStatus.WaitingForApproval;
	}

	private get canEdit() {
		const { isPriorFollowUp } = this.state;
		const { readonlyContent, disableCampaignEdits } = this.props;
		return !readonlyContent && !isPriorFollowUp && !disableCampaignEdits;
	}

	private get sendRequiresCompliance() {
		const { emailComposer } = this.props;
		const resourceEmailComposer = emailComposer as ComposeResourceEmailViewModel;

		// If we're coming from a ResourceSelector at all, we will not require compliance.
		if (resourceEmailComposer && resourceEmailComposer.resourceSelector) {
			return false;
		}

		return (
			!this.showForApproval && accountHasComplianceEnabled(this.props.userSession, this.props.impersonationContext)
		);
	}

	private onEditHtmlNewsletterTemplateClicked = () => {
		const { emailBodyTemplate } = this.state;
		const { history, emailComposer } = this.props;
		const templateId = emailBodyTemplate?.id || emailComposer.emailMessage.templateReference?.templateId;
		if (templateId) {
			// nav back to template editor
			const locationState: Models.ILocationState<any, Api.ITemplate> = {
				model: emailBodyTemplate,
			};
			history?.push({
				pathname: `/campaigns/htmlnewsletter/${templateId}`,
				state: locationState,
			});
		}
	};

	private resetAndReloadContactsList = (refreshList = false) => {
		const { emailComposer, logEvent, logApiError } = this.props;

		if (this.resourceEmailComposer) {
			emailComposer?.resetRecipientsResultsList();
			const promise = this.resourceEmailComposer.getNextBatchOfRecipients();
			if (promise) {
				logEvent('ContactsLoad');
				promise.catch((error: Api.IOperationResultNoValue) => {
					logApiError('ContactsLoad-Error', error);
				});
			}

			const approxPromise = this.resourceEmailComposer.resourceSelector
				? this.resourceEmailComposer.getEmailApproximationWithResource()
				: emailComposer.emailMessage.getEmailApproximation();

			approxPromise?.catch((error: Api.IOperationResultNoValue) => {
				logApiError('HasEmailApproximationLoad-Error', error);
			});
		} else if (refreshList && emailComposer?.allowContactFilterTagSearchChanges) {
			emailComposer?.reloadRecipientsList();
		}
	};

	private get isFromHtmlNewsletterTemplate() {
		const { emailBodyTemplate } = this.state;
		const { emailComposer } = this.props;
		const content =
			(typeof emailBodyTemplate === 'object' && emailBodyTemplate?.content) || emailComposer.emailMessage?.content;
		return content?.sourceFormat === Api.ContentSourceFormat.UnlayerHtmlNewsletter;
	}

	private canDelete() {
		const { emailBodyTemplate } = this.state;
		return (
			emailBodyTemplate?.scope === Api.TemplateScope.User || emailBodyTemplate?.scope === Api.TemplateScope.Account
		);
	}

	private onDeleteTemplate = async (shouldDelete: boolean) => {
		const { userSession } = this.props;
		const { emailBodyTemplate } = this.state;
		const nextState: IState = {
			showDeleteConfirmation: false,
		};
		if (shouldDelete) {
			if (emailBodyTemplate.creator?.id === userSession.user.id || adminRegExp.test(userSession.user.role)) {
				await this.mTemplates.delete(emailBodyTemplate);
				// TODO: handle the possible error, do not redirect and close the confirmation.
			} else {
				hideDefaultMessageTemplate(emailBodyTemplate.id);
			}
			nextState.redirectTo = '/campaigns';
		}
		this.setState(nextState);
	};

	private onDeleteClick = async () => {
		this.setState({ showDeleteConfirmation: true });
	};

	private onGoBackFromEditMode = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
		const { hasEdited } = this.state;
		e.preventDefault();
		if (hasEdited) {
			this.setState({ showNavigateConfirmation: true });
		} else {
			// Clear session when modal is closed without going to unified success
			CampaignSessionManager.clearSession();
			this.props.fullscreenModal?.dismissModal();
		}
	};

	private onGoBackFromPreviewMode = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
		const { fullscreenModal, isQueuedCampaign } = this.props;
		const { hasEdited } = this.state;
		e.preventDefault();
		if (!isQueuedCampaign && hasEdited) {
			this.setState({ showNavigateConfirmation: true });
		} else {
			fullscreenModal.history.goBack();
		}
	};

	private cleanupUnusedRecipientAttachmentsAsync = () => {
		const { emailComposer } = this.props;
		if (!emailComposer.campaign?.id) {
			const recentlyAddedAttachments = emailComposer.emailMessage
				?.getAllRecipientSpecificAttachments()
				.filter(x => Boolean((x as Api.IInProgressFileAttachment).uploadTaskId)) as Api.IInProgressFileAttachment[];
			if (recentlyAddedAttachments?.length) {
				return Promise.all(
					recentlyAddedAttachments.map(x =>
						emailComposer.emailMessage.deleteSavedEmailAttachmentAsync(x as Api.IFileAttachment)
					)
				);
			}
		}
	};

	private onNavigateConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		const { emailComposer, onShowOmitConfirmation, fullscreenModal } = this.props;
		const { hasEdited, navigateAway } = this.state;
		if (result && !canceled && result.isDestructive) {
			if (navigateAway && emailComposer?.emailMessage?.contactsToOmit?.length === 0) {
				this.cleanupUnusedRecipientAttachmentsAsync();
				navigateAway();
			} else if (emailComposer?.emailMessage?.contactsToOmit?.length > 0 && onShowOmitConfirmation) {
				onShowOmitConfirmation(undefined, hasEdited);
			} else {
				this.cleanupUnusedRecipientAttachmentsAsync();
				// Clear session when modal is closed without going to unified success
				CampaignSessionManager.clearSession();
				fullscreenModal.dismissModal();
			}
		}
		this.setState({ navigateAway: null, showNavigateConfirmation: false });
	};

	private onCloseButtonClicked = (e: React.MouseEvent<HTMLElement>) => {
		const { emailComposer, onShowOmitConfirmation } = this.props;
		const { hasEdited } = this.state;

		if (emailComposer?.emailMessage?.contactsToOmit?.length > 0 && onShowOmitConfirmation) {
			e.preventDefault();
			onShowOmitConfirmation(e, hasEdited);
		} else {
			if (emailComposer?.emailMessage?.contactsToOmit?.length > 0) {
				emailComposer?.emailMessage?.contactsToOmit?.clear();
				if (emailComposer?.emailMessage?.options?.followUp?.excludeContactIds) {
					emailComposer.emailMessage.options.followUp.excludeContactIds = [];
				}
			}
			if (this.shouldShowComposer) {
				this.onGoBackFromEditMode(e);
			} else {
				this.onGoBackFromPreviewMode(e);
			}
		}
	};

	private onHeaderBackButtonClicked = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
		const { emailComposer, onShowOmitConfirmation } = this.props;
		if (this.action !== 'automation' && emailComposer?.selectedCustomEmail) {
			// must prevent default explicitly otherwise the FS modal will close
			e.preventDefault();

			return this.returnToRecipientsList();
		}

		if (emailComposer?.emailMessage?.contactsToOmit?.length > 0 && onShowOmitConfirmation) {
			e.preventDefault();
			onShowOmitConfirmation(e);
		} else {
			if (this.shouldShowComposer) {
				this.onGoBackFromEditMode(e);
			} else {
				this.onGoBackFromPreviewMode(e);
			}
		}
	};

	private renderEmailComposer() {
		const { canShowTemplateSelect, emailComposer, userSession } = this.props;
		const { bodyError, emailBodyEditorState, emailBodyTemplate, hideTemplateSaveButton, subject, subjectError } =
			this.state;

		const showTemplateSaveButton = !hideTemplateSaveButton && !emailComposer?.selectedCustomEmail;
		const hideBodyTemplateSelectorButton =
			canShowTemplateSelect === false &&
			!customRegExp.test(emailComposer?.resourceSelectorId) &&
			!TurningXXRexExp.test(emailComposer?.resourceSelectorId) &&
			!emailComposer?.isFollowUp;

		const hideEditSignature =
			emailComposer?.campaign?.creator && userSession.user.id !== emailComposer.campaign.creator?.id;
		return (
			<div
				className={css(
					styleSheet.bodyEditorContainer,
					showTemplateSaveButton ? styleSheet.bodyEditorContainerWithTemplateSaveButton : null
				)}
			>
				<SimpleEmailComposer
					bodyContentFooter={this.renderContentCreationTipsPopover()}
					bodyEditorState={emailBodyEditorState}
					bodyError={bodyError}
					bodyFooter={this.renderEmailComposerFooter()}
					bodyTemplate={emailBodyTemplate}
					className={css(styleSheet.bodyEditor)}
					emailComposer={emailComposer}
					emailMessage={emailComposer?.emailMessage}
					hideBodyTemplateSelectorButton={hideBodyTemplateSelectorButton}
					hideEditSignature={hideEditSignature}
					onBodyEditorStateChanged={this.onBodyEditorStateChanged}
					onBodyTemplateChanged={this.onBodyTemplateChanged}
					onRequestRemoveSavedEmailAttachment={this.onRequestRemoveSavedEmailAttachment}
					onSignatureTemplateChanged={this.onSignatureTemplateChanged}
					onSubjectChanged={this.onSubjectChanged}
					onCustomFileChanged={this.onCustomFileChanged}
					savedAttachments={
						emailComposer?.selectedCustomEmail
							? (emailComposer?.selectedCustomEmail?.attachments?.filter(
									x => !(x as Api.IInProgressFileAttachment).uploadTaskId
								) as Api.IFileAttachment[])
							: emailComposer?.emailMessage?.savedAttachments
					}
					signatureTemplate={emailComposer?.emailMessage?.signatureTemplate}
					subject={subject}
					subjectError={subjectError}
					templates={this.mTemplates}
				/>
				{emailComposer?.selectedCustomEmailContact ? (
					<div className={css(baseStyleSheet.horizontalStack, styleSheet.bodyEditorFooter)}>
						<button className={css(baseStyleSheet.ctaButton)} onClick={this.onFinishEditingContentClicked}>
							<span>{`${emailComposer?.selectedCustomEmail ? 'Save and go ' : 'Go '}back to all recipients`}</span>
						</button>
					</div>
				) : null}
				{this.renderKeyDateMessage()}
			</div>
		);
	}

	private onRequestRemoveSavedEmailAttachment = (savedAttachment: Api.IFileAttachment) => {
		const { emailComposer } = this.props;
		const promise = emailComposer?.emailMessage.deleteSavedEmailAttachmentAsync(savedAttachment);
		promise?.then(() => {
			if (emailComposer?.selectedCustomEmail) {
				emailComposer.selectedCustomEmail.attachments = emailComposer.selectedCustomEmail?.attachments.filter(
					x => x.id !== savedAttachment.id
				);
			}
		});
		return promise;
	};

	private get willAddTemplateToUser() {
		const { emailComposer, userSession, initialEmailBodyTemplate } = this.props;
		const { hasEdited } = this.state;

		const emailBodyTemplateIsUndefinedOrSystemTemplate =
			!Api.TemplatesViewModel.userHasTemplateForKeyDateKind(userSession, this.keyDateKind) ||
			(initialEmailBodyTemplate &&
				emailComposer?.emailMessage?.templateReference?.templateId === initialEmailBodyTemplate.id &&
				initialEmailBodyTemplate.scope !== Api.TemplateScope.User);
		return (
			this.keyDateKind &&
			!emailComposer?.selectedCustomEmail &&
			emailBodyTemplateIsUndefinedOrSystemTemplate &&
			hasEdited
		);
	}

	private renderEmailComposerFooter() {
		const { emailComposer } = this.props;
		if (emailComposer?.selectedCustomEmail) {
			return null;
		} else {
			const message = `${FirstNamePlaceholder.symbol} is a placeholder for recipient names; actual names will be populated.`;
			return (
				<span className={css(baseStyleSheet.truncateText, styleSheet.emailComposerFooter)} title={message}>
					{message}
				</span>
			);
		}
	}

	private onReadonlyEditorExecuteCommand = (
		e: React.ChangeEvent<any> & {
			command: string;
			type: string;
			ui?: any;
			value?: any;
		}
	) => {
		const target = e.value?.sourceEvent?.target as HTMLElement;

		if (target && e.command === Models.levPlaceholderPopoverCommand) {
			this.setState({
				levPlaceholderPopover: {
					coordinate: {
						x: target.offsetLeft + target.getBoundingClientRect().width,
						y: target.offsetTop + target.getBoundingClientRect().height / 2.0,
					},
					placeholderValue: e.value?.placeholderValue,
				},
			});
		} else if (e.command === Models.levPlaceholderPopoverCloseCommand) {
			this.onDismissLevPopover();
		}
	};

	private onDismissLevPopover = () => {
		this.setState({
			levPlaceholderPopover: null,
		});
	};

	private onRenderRecipientsSelect = () => {
		const { contextFinishedEditingCtaText, emailComposer, disableContactEdits, impersonationContext } = this.props;

		return (
			<Observer>
				{() => {
					const nextButtonText =
						contextFinishedEditingCtaText ||
						(this.action === 'automation' && this.keyDateKind !== 'Renewal'
							? `Schedule Automation${this.resourceEmailComposer?.recipients?.length > 1 ? 's' : ''}`
							: `Next: ${
									this.showForApproval
										? 'Approve Send Time'
										: this.sendRequiresCompliance
											? 'Compliance Options'
											: emailComposer.isSuggestion
												? 'Okay'
												: 'Send Options'
								}`);

					return (
						<div className={css(styleSheet.contextContainer)}>
							<EditCampaignRecipients onContactEditButtonClicked={this.onContactEditButtonClicked}>
								<TagEmailRecipientsList
									disableContactAdding={
										!!emailComposer?.campaign?.id || impersonationContext?.isValid || !!this.keyDateKind
									}
									disableContactEdits={
										disableContactEdits ||
										this.action === 'automation' ||
										(emailComposer?.canSendOnBehalf &&
											(emailComposer?.emailMessage?.isSendFromOption(Api.SendEmailFrom.ContactOwner) ||
												emailComposer?.emailMessage?.isSendFromOption(Api.SendEmailFrom.SelectedUser))) ||
										this.state.isPriorFollowUp
									}
									disableContactRemoval={this.state.isPriorFollowUp}
									emailComposer={emailComposer}
									key='recipients'
									onContactSelected={this.onContactSelected}
									onScrollerRef={this.onRecipientsListScrollerRef}
									resetOnMount={!this.mMounted}
									sendFromOption={this.state.selectedSendFromOption}
									selectedEmployee={this.state.selectedEmployee}
									styles={[styleSheet.recipientsList]}
									ctaButtonText={nextButtonText}
									onCtaClick={this.onContextFinishedEditingClicked}
									initialOwnershipFilter={
										emailComposer?.emailMessage?.contactsFilterRequest?.ownershipFilter ??
										emailComposer?.emailMessage?.contactsFilterRequest?.calculatedOwnershipFilter
									}
									disableOwnershipFilter={!!emailComposer?.campaign?.id}
								/>
								{this.renderApprovalContextFooter()}
							</EditCampaignRecipients>
						</div>
					);
				}}
			</Observer>
		);
	};

	private renderApprovalContextFooter() {
		const { onRejectClicked, impersonationContext } = this.props;
		return (
			this.showForApproval && (
				<div className={css(styleSheet.recipientsApprovalFooter)}>
					<button className={css(baseStyleSheet.linkDestructive)} onClick={onRejectClicked}>
						<span>{`${impersonationContext?.isValid ? 'Cancel' : 'Reject'} Campaign`}</span>
					</button>
				</div>
			)
		);
	}

	private renderContentCreationTipsPopover() {
		return this.state.canShowContentCreationTips ? <EditCampaignContentCreationTips /> : null;
	}

	private renderKeyDateMessage() {
		const kind = this.keyDateKind;
		if (kind && !this.willAddTemplateToUser) {
			return null;
		}

		let kindDescription: string = null;
		switch (kind) {
			case Api.KeyDateKind.Anniversary: {
				kindDescription = 'anniversaries';
				break;
			}
			case Api.KeyDateKind.Birthday: {
				kindDescription = 'birthdays';
				break;
			}
			case Api.KeyDateKind.Renewal: {
				kindDescription = 'renewals';
				break;
			}
			default: {
				kindDescription = null;
				break;
			}
		}
		if (!kindDescription) {
			return null;
		}
		return (
			<span className={css(styleSheet.keyDateMessage)}>
				{this.willAddTemplateToUser && `We'll use this template for ${kindDescription} going forward!`}
			</span>
		);
	}

	private onStartAutomationBannerClicked = async () => {
		this.setState({
			navigateAway: () => {
				const { history } = this.props;
				// navigate to the automation flow
				history.push({
					pathname: `/automations/new`,
				});
			},
			showNavigateConfirmation: true,
		});
	};

	private get action(): 'edit' | 'automation' {
		return this.state.selectedAutomationTemplate ? 'automation' : 'edit';
	}

	private onRecipientsListScrollerRef = (ref?: HTMLElement) => {
		this.mRecipientListScrollerRef = ref;
	};

	private onContactSelected = (contact: Api.ContactViewModel) => {
		const { emailComposer, history } = this.props;
		const { subject: storedSubject, emailBodyEditorState: storedEmailBodyEditorState } = this.state;

		const nextState: IState = {
			bodyError: null,
			editing: true,
			subjectError: null,
		};
		if (
			emailComposer?.emailMessage?.options?.sendEmailFrom === Api.SendEmailFrom.ContactOwner &&
			emailComposer.allowContactFilterTagSearchChanges &&
			!emailComposer.campaign?.categories?.includes(Api.EmailCategories.ResourceSelector) &&
			!this.resourceEmailComposer?.resourceSelector
		) {
			// only show selected contact info in context container instead of
			// allowing edits to individual content
			emailComposer.selectedCustomEmailContact = contact;
		} else {
			if (this.action === 'automation') {
				emailComposer?.selectCustomEmailForContact(contact, true);
			} else {
				nextState.selectedCustomEmailHasCustomContent =
					!!emailComposer?.emailMessage?.getCustomMessageForContact(contact)?.content;
				emailComposer?.emailMessage?.contactsToOmit?.removeItems([contact]);
				// save the base content and subject
				if (emailComposer?.emailMessage) {
					emailComposer.emailMessage.content = storedEmailBodyEditorState?.getRawRichTextContent();
					emailComposer.emailMessage.subject = storedSubject;
				}

				// try to load customized email content, if any
				const selectedCustomEmail = emailComposer?.selectCustomEmailForContact(contact, true);
				nextState.emailBodyEditorState = convertRawRichTextContentStateToRichContentEditorState(
					selectedCustomEmail?.content
						? selectedCustomEmail.content
						: storedEmailBodyEditorState?.getRawRichTextContent()
				);
				nextState.subject = selectedCustomEmail.subject || storedSubject;
			}
		}

		// save scroll offset
		if (this.mRecipientListScrollerRef) {
			nextState.lastContactListScrollOffset = this.mRecipientListScrollerRef.scrollTop;
		}

		this.setState(nextState, () => {
			history?.push({
				pathname: `${history.location.pathname}/${contact.id}`,
				state: history.location.state,
			});
		});
	};

	private onContactEditButtonClicked = (contact: Api.ContactViewModel) => {
		this.setState({ previousContactFirstName: contact.firstName });
	};

	private showRecipients = () => {
		const { history, match, emailComposer, userSession } = this.props;

		if (
			emailComposer?.emailMessage &&
			!emailComposer.emailMessage?.contactsFilterRequest &&
			emailComposer.emailMessage?.contactsToAdd?.length === 0
		) {
			emailComposer.emailMessage.contactsFilterRequest = {
				contactFilterRequest: applyDefaultExcludedTagsToContactsFilterRequest(emailComposer, {
					criteria: [
						{
							property: Api.ContactFilterCriteriaProperty.OwnedBy,
							value: userSession?.user.id,
						},
					],
				}),
			};
		}

		history?.push(`${match.url}/recipients`);
	};

	private startEditing = () => {
		const { emailComposer } = this.props;
		emailComposer.editModeIsActive = true;
		this.setState({
			editing: true,
		});
	};

	private onContextFinishedEditingClicked = (e: React.MouseEvent<HTMLElement>) => {
		const { history, match, onContextFinishedEditingClicked, emailComposer } = this.props;
		const { selectedAutomationTemplate } = this.state;

		if (this.action === 'automation') {
			emailComposer.selectedAutomationTemplate = selectedAutomationTemplate;
			if (selectedAutomationTemplate) {
				history?.push(`${match.url}/send-options`);
			}
		} else {
			emailComposer.selectedAutomationTemplate = null;
			this.finishEditingContent();
			if (!this.validateBaseEmailBeforeSending()) {
				return;
			}
			onContextFinishedEditingClicked?.(e);
			if (!e.isDefaultPrevented() && !this.showForApproval) {
				if (this.sendRequiresCompliance) {
					history?.push(`${match.url}/compliance-options`);
				} else {
					history?.push(`${match.url}/send-options`);
				}
			}
		}
	};

	private onFinishEditingContentClicked = (e: React.MouseEvent<HTMLElement>) => {
		this.finishEditingContent();
		this.props.onContentFinishedEditingClicked?.(e);
	};

	public validateBaseEmailBeforeSending = () => {
		const { emailBodyEditorState, subject, emailBodyTemplate } = this.state;
		const { emailComposer, errorMessages } = this.props;
		if (!subject) {
			this.showEmailError(ComposeEmailError.NoSubject);
			return false;
		}

		const content = emailBodyTemplate?.content || emailComposer.emailMessage.content;
		if (content?.sourceFormat !== Api.ContentSourceFormat.UnlayerHtmlNewsletter) {
			if (!emailBodyEditorState || !emailBodyEditorState.hasContent()) {
				this.showEmailError(ComposeEmailError.NoContent);
				return false;
			}

			const rawContent = emailBodyEditorState.getRawRichTextContent();
			if (!Token.isFirstNameTokenFormattedCorrectlyLoose(rawContent)) {
				errorMessages.push({
					messages: [
						`The first name token does not appear to be formatted properly. It should be ${FirstNamePlaceholder.symbol}.`,
					],
				});
				return false;
			}
		}

		/**
		 * @todo - Move this to VM?
		 *
		 * @todo - Add logic for survey followup checks
		 */

		if (emailComposer?.isFollowUp) {
			if (
				emailComposer.followUpSource instanceof Api.CampaignEmailViewModel &&
				!emailComposer?.emailMessage?.options?.followUp?.statuses
			) {
				errorMessages?.push({
					messages: ['Please select at least one recipient group.'],
				});
				return false;
			}
		} else if (emailComposer?.recipients?.length === 0) {
			errorMessages?.push({
				messages: ['Please select at least one recipient.'],
			});
			return false;
		}

		const emailAttachments = emailComposer?.emailMessage?.attachments;
		const savedAttachments = emailComposer?.emailMessage?.savedAttachments?.filter(x => !x.embedded);
		const hasExceededMaxByteCount =
			emailAttachments?.hasExceededMaxByteCount ||
			emailAttachments?.byteCount + (savedAttachments || []).reduce((count, x) => count + x.fileSize, 0) >
				emailAttachments?.maxByteCount;
		if (hasExceededMaxByteCount) {
			errorMessages?.push({
				messages: [
					`You've exceeded the attachment size limit of ${getFileSizeStringValue(emailAttachments.maxByteCount)}.`,
				],
			});
			return false;
		}

		return true;
	};

	public finishEditingContent = () => {
		const { emailComposer } = this.props;
		const { subject, emailBodyEditorState, hasEdited, selectedCustomEmailHasCustomContent } = this.state;
		if (emailComposer?.selectedCustomEmailContact) {
			if (
				emailComposer?.emailMessage?.options?.sendEmailFrom !== Api.SendEmailFrom.ContactOwner ||
				(emailComposer?.emailMessage?.options?.sendEmailFrom === Api.SendEmailFrom.ContactOwner &&
					!emailComposer?.allowContactFilterTagSearchChanges) ||
				this.resourceEmailComposer?.resourceSelector
			) {
				if (emailComposer?.selectedCustomEmail && selectedCustomEmailHasCustomContent && emailBodyEditorState) {
					this.saveToEmail(emailComposer.selectedCustomEmail, emailComposer.selectedCustomEmailContact);
				}
			}
			this.returnToRecipientsList();
			return;
		}
		runInAction(() => {
			if (emailComposer?.emailMessage) {
				emailComposer.emailMessage.subject = subject;
				emailComposer.emailMessage.content = emailBodyEditorState?.getRawRichTextContent();

				if (this.state.emailBodyTemplate?.schedule?.expirationDate != null) {
					emailComposer.emailMessage.options.scheduledSend.expirationDate =
						this.state.emailBodyTemplate?.schedule?.expirationDate;
				}

				if (emailComposer.emailMessage.templateReference) {
					emailComposer.emailMessage.templateReference.isCustomized = hasEdited;
				}
			}
			emailComposer.editModeIsActive = false;
			this.setState({
				editing: false,
			});
		});
	};

	private onSubjectChanged = (subject: string) => {
		const { emailComposer } = this.props;
		const { selectedCustomEmailHasCustomContent } = this.state;
		const nextState: IState = {
			bodyError: null,
			subject,
			subjectError: null,
		};
		if (emailComposer?.selectedCustomEmail) {
			if (!selectedCustomEmailHasCustomContent && emailComposer.selectedCustomEmail.subject !== subject) {
				// user changed subject... this is now a custom email message
				nextState.selectedCustomEmailHasCustomContent = true;
			}
		} else {
			if (emailComposer?.emailMessage) {
				emailComposer.emailMessage.subject = subject;
			}
			nextState.hasEdited = true;
		}

		this.setState(nextState);
	};

	private onCustomFileChanged = () => {
		const { emailComposer } = this.props;
		const nextState: IState = {
			bodyError: null,
			subjectError: null,
		};
		nextState.selectedCustomEmailHasCustomContent = true;
		nextState.hasEdited = true;

		emailComposer.emailMessage?.setCustomMessageForContact(emailComposer.selectedCustomEmailContact, {
			...emailComposer.selectedCustomEmail,
			attachments:
				emailComposer.selectedCustomEmail?.attachments?.length > 0
					? emailComposer.selectedCustomEmail?.attachments
					: null,
		});

		this.setState(nextState);
	};

	private onBodyEditorStateChanged = (emailBodyEditorState: Api.IRichContentEditorState) => {
		const { emailComposer } = this.props;
		const { hideTemplateSaveButton, selectedCustomEmailHasCustomContent, canShowContentCreationTips } = this.state;
		const nextState: IState = {
			bodyError: null,
			emailBodyEditorState,
			subjectError: null,
		};
		if (emailComposer?.selectedCustomEmail && !selectedCustomEmailHasCustomContent) {
			// flag it as having custom content... when the user hits the back button, we'll save the editor state as raw content to the vm... no need to do it now
			nextState.selectedCustomEmailHasCustomContent =
				emailBodyEditorState.getRawRichTextContent() !== this.state.emailBodyEditorState.getRawRichTextContent();
		}
		// note: once the text has changed (not just selection), we don't need to compare plain text
		if (
			canShowContentCreationTips ||
			(hideTemplateSaveButton && emailBodyEditorState && this.state.emailBodyEditorState)
		) {
			const hide =
				emailBodyEditorState.getRawRichTextContent() === this.state.emailBodyEditorState.getRawRichTextContent();
			nextState.hideTemplateSaveButton = hide;
			nextState.canShowContentCreationTips = hide;
		}
		nextState.hasEdited = true;

		this.setState(nextState);
	};

	private returnToRecipientsList = () => {
		const { emailComposer, history } = this.props;
		const { lastContactListScrollOffset } = this.state;

		const nextState: IState = {
			selectedCustomEmailHasCustomContent: false,
		};

		if (
			emailComposer?.emailMessage?.options?.sendEmailFrom === Api.SendEmailFrom.ContactOwner &&
			emailComposer?.allowContactFilterTagSearchChanges &&
			!this.resourceEmailComposer?.resourceSelector
		) {
			// just clear the selected contact
			if (emailComposer) {
				emailComposer.selectedCustomEmailContact = null;
			}
		} else {
			// restore the base content and subject
			nextState.emailBodyEditorState =
				convertRawRichTextContentStateToRichContentEditorState(emailComposer?.emailMessage?.content) ||
				getDefaultBulkMessagingBodyEditorState();
			nextState.subject = emailComposer?.emailMessage?.subject;

			emailComposer?.selectCustomEmailForContact(null);
		}

		this.setState(nextState, () => {
			if (this.isAIAssistantOpen()) {
				// Since ai assistant step is implemented as a route and gets in the middle of previous steps implemented as routes,
				// wherever we needed to go back a step as part of functionality before, we need to go back 2 steps now to jump above the ai assistant step when it's open.
				history.go(-2);
			} else {
				history?.goBack();
			}
			if (
				lastContactListScrollOffset !== null &&
				lastContactListScrollOffset !== undefined &&
				this.mRecipientListScrollerRef
			) {
				this.mRecipientListScrollerRef.scrollTop = lastContactListScrollOffset;
				this.setState({
					lastContactListScrollOffset: null,
				});
			}
		});
	};

	private saveToEmail = (email: Api.IEmailMessageComposeContact, recipient?: Api.ContactViewModel) => {
		const { emailComposer } = this.props;
		const { emailBodyEditorState, subject } = this.state;
		if (email) {
			email.content = emailBodyEditorState?.getRawRichTextContent();
			email.subject = subject;
			emailComposer?.emailMessage?.setCustomMessageForContact(recipient, email);
		}
	};

	private onBodyTemplateChanged = (template: Api.ITemplate) => {
		const { emailComposer } = this.props;
		if (template) {
			const { emailMessage } = emailComposer;
			emailMessage.subject = template.subject ?? emailMessage.subject;
			emailMessage.setSavedAttachments(template.attachments);
			this.setState({
				emailBodyEditorState: convertRawRichTextContentStateToRichContentEditorState(template.content),
				emailBodyTemplate: template,
				subject: emailMessage.subject,
				translatedEmailBodyTemplate: null,
				hasEdited: false,
			});
		}

		if (this.keyDateKind) {
			emailComposer?.loadAlternateKeyDateTemplates()?.catch((err: Api.IOperationResultNoValue) => {
				const { logApiError } = this.props;
				logApiError('LoadAlternateKeyDateTemplates', err);
			});
		}
	};

	private onSignatureTemplateChanged = (signatureTemplate: Api.ITemplate) => {
		const { emailComposer } = this.props;
		emailComposer.emailMessage.signatureTemplate = emailComposer?.emailMessage ? signatureTemplate : null;
	};

	/**
	 * Load the default signature template for the user.
	 * @param {boolean} [shouldOverwriteExistingSignature=true] - DEFAULTS to true - If true, the existing signature template will be overwritten.
	 */
	private loadDefaultSignature = () => {
		const { logApiError, logEvent, emailComposer } = this.props;
		if (this.mTemplates?.impersonationContext && !this.mTemplates.impersonationContext.user?.id) {
			// Impersonating, but we don't have a user. Cannot load a signature template
			return Promise.resolve<Api.ITemplate[]>([]);
		}
		this.mTemplates.signatureTemplates.reset();
		const promise = this.mTemplates.signatureTemplates.getNext();
		if (promise) {
			logEvent('SignatureTemplatesLoad');
			promise
				.then(() => {
					const signatureTemplate = this.mTemplates.signatureTemplates.fetchResults.find(x => !!x.defaultTemplate);
					emailComposer.emailMessage.signatureTemplate = signatureTemplate ?? null;
				})
				.catch((error: Api.IOperationResultNoValue) => {
					logApiError('SignatureTemplatesLoad-Error', error);
				});
			return promise;
		}
	};

	private async loadUnsubscribeSignatureTemplate() {
		const { logApiError, logEvent } = this.props;
		try {
			logEvent('LoadUnsubscribeTemplate');
			await this.mUnsubscribeTemplate.loadUserTemplate();
			if (this.mUnsubscribeTemplate.userTemplate) {
				this.setState({
					unsubscribeSignatureTemplate: this.mUnsubscribeTemplate.userTemplate,
				});
			} else {
				await this.mUnsubscribeTemplate.loadAccountTemplate();
				this.setState({
					unsubscribeSignatureTemplate: this.mUnsubscribeTemplate.accountTemplate,
				});
			}
		} catch (error) {
			logApiError('LoadUnsubscribeTemplate-Error', error);
		}
	}

	private loadAutomationTemplates = async () => {
		const { logApiError, logEvent, userSession, impersonationContext } = this.props;
		if (this.resourceEmailComposer?.resourceSelector && userSession.account.features?.automation?.enabled) {
			try {
				this.setState({ isLoadingAutomationTemplates: true });
				logEvent('AutomationTemplatesLoad', {
					resourceSelectorId: this.resourceEmailComposer.resourceSelector,
				});

				const url = Api.ImpersonationBroker.composeApiUrl({
					urlPath: `automationTemplate/resourceSelector/${encodeURIComponent(
						this.resourceEmailComposer.resourceSelector
					)}`,
					impersonationContext,
				});

				const templates = await userSession.webServiceHelper.callAsync<Api.IAutomationTemplate[]>(url, 'GET');

				this.setState(
					{
						automationTemplatesForResourceSelectorId: templates,
						isLoadingAutomationTemplates: false,
					},
					() => {
						this.loadInitialBodyTemplate();
					}
				);
			} catch (error) {
				this.setState({ isLoadingAutomationTemplates: false });
				logApiError('AutomationTemplatesLoad-Error', error);
			}
		}
	};

	private loadInitialBodyTemplate = () => {
		const { logApiError, logEvent, errorMessages, initialEmailBodyTemplate, emailComposer } = this.props;
		const { automationTemplatesForResourceSelectorId } = this.state;

		// becomes the selected template in the UI if it exists
		const firstAutomationTemplate = automationTemplatesForResourceSelectorId?.[0];
		if (firstAutomationTemplate) {
			this.resetGroupByHouseholdFromCurrentAutomationTemplate(firstAutomationTemplate);
		}
		if (initialEmailBodyTemplate?.id && !initialEmailBodyTemplate?.content) {
			logEvent('EmailBodyTemplateLoad');
			this.mTemplates
				.getById(initialEmailBodyTemplate.id)
				.then(emailBodyTemplate => {
					this.setState(
						{
							emailBodyTemplate,
							selectedAutomationTemplate: firstAutomationTemplate,
						},
						() => {
							if (emailBodyTemplate.templateType === Api.TemplateType.HtmlNewsletter) {
								emailComposer.canCustomizeIndividualEmails = false;
							}
						}
					);
				})
				.catch((error: Api.IOperationResultNoValue) => {
					logApiError('EmailBodyTemplateLoad-Error', error);
					errorMessages.pushApiError(error);
				});
		} else {
			this.setState({
				selectedAutomationTemplate: firstAutomationTemplate,
			});
		}
		if (firstAutomationTemplate) {
			emailComposer.canCustomizeIndividualEmails = false;
		}
	};

	private showEmailError = (error: ComposeEmailError) => {
		let hasError = false;
		const nextState: IState = {
			bodyError: null,
			subjectError: null,
		};

		if (error !== ComposeEmailError.NoError) {
			if (error === ComposeEmailError.NoSubject) {
				nextState.subjectError = 'A subject is required';
			}
			if (error === ComposeEmailError.NoContent) {
				nextState.bodyError = 'Message content is required';
			}

			hasError = true;
			nextState.editing = true;
		}

		if (hasError) {
			this.setState(nextState);
		}
	};
}

const EditCampaignAsObserver = observer(_EditCampaign);
const EditCampaignWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey,
	Models.ImpersonationContextKey,
	FullScreenModalViewModelKey
)(EditCampaignAsObserver);
const EditCampaignWithRouter = withRouter(EditCampaignWithContext);
const EditCampaignWithConnectionTypes = withConnectionTypes(EditCampaignWithRouter, { refetchOnWindowFocus: false });
export const EditCampaign = withEventLogging(EditCampaignWithConnectionTypes, 'EditCampaign');
